import { useState, useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const contactInfo = [
  {
    icon: 'ri-map-pin-2-line',
    label: '公司地址',
    primary: '北京市朝阳区东四环中路82号3座19层',
    secondary: '',
    color: '#F97316',
  },
  {
    icon: 'ri-mail-line',
    label: '商务邮箱',
    primary: 'service@broadmind.cn',
    secondary: '一般 24 小时内回复',
    color: '#F97316',
  },
  {
    icon: 'ri-wechat-line',
    label: '微信公众号',
    primary: '宽思智能',
    secondary: '扫码关注获取最新动态',
    color: '#2563EB',
  },
];

const cooperationTypes = [
  { value: 'ai-strategy', label: 'AI 战略咨询' },
  { value: 'ai-training', label: 'AI 研发培训' },
  { value: 'custom-dev', label: '工具软件定制开发' },
  { value: 'legacy-upgrade', label: '老系统升级改造' },
  { value: 'agent-platform', label: 'Agent 平台定制' },
  { value: 'other', label: '其他合作' },
];

const teamSizes = [
  { value: '1-10', label: '1–10 人' },
  { value: '11-50', label: '11–50 人' },
  { value: '51-200', label: '51–200 人' },
  { value: '200+', label: '200 人以上' },
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    teamSize: '',
    cooperationType: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );
    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'message') {
      if (value.length > 500) return;
      setCharCount(value.length);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setSubmitting(true);
    const teamSizeLabel = teamSizes.find((s) => s.value === formData.teamSize)?.label || '';
    const cooperationLabel =
      cooperationTypes.find((t) => t.value === formData.cooperationType)?.label || '';
    const subject = encodeURIComponent(
      `预约交流：${formData.company || formData.name || '咨询'}`
    );
    const body = encodeURIComponent(
      `姓名：${formData.name}\n` +
        `公司：${formData.company}\n` +
        `邮箱：${formData.email}\n` +
        `手机：${formData.phone}\n` +
        `团队规模：${teamSizeLabel}\n` +
        `合作类型：${cooperationLabel}\n\n` +
        `想聊的内容：\n${formData.message}`
    );
    window.location.href = `mailto:service@broadmind.cn?subject=${subject}&body=${body}`;
    setSubmitted(true);
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: '\'Noto Sans SC\', Inter, sans-serif' }}>
      <Navbar />

      {/* Hero */}
      <section className="relative bg-[#0F172A] pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full bg-[#2563EB] blur-3xl" />
          <div className="absolute bottom-10 right-1/4 w-64 h-64 rounded-full bg-[#F97316] blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/20 text-white/60 text-sm mb-6">
            <i className="ri-chat-1-line text-[#F97316]" />
            开始一次真正的对话
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight">
            联系宽思智能
          </h1>
          <p className="text-lg text-white/60 max-w-xl mx-auto leading-relaxed">
            无论你处于哪个阶段——刚开始思考、已有明确需求，还是想先了解方法论，我们都欢迎你来聊。
          </p>
        </div>
      </section>

      {/* Main Content: Form + Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

            {/* Form */}
            <div
              ref={(el) => { sectionRefs.current[1] = el; }}
              className="opacity-0 translate-y-8 transition-all duration-700"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-2">预约交流</h2>
              <p className="text-[#64748B] text-sm mb-8">填写以下信息，我们会在 1 个工作日内与你联系。</p>

              {submitted ? (
                <div className="rounded-2xl border border-slate-100 bg-[#F8FAFC] p-10 text-center">
                  <div className="w-14 h-14 rounded-full bg-[#F97316]/10 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-2xl text-[#F97316]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#0F172A] mb-2">已为你打开邮件客户端</h3>
                  <p className="text-[#64748B] text-sm leading-relaxed">
                    请发送邮件至 <a className="text-[#F97316] underline" href="mailto:service@broadmind.cn">service@broadmind.cn</a>，<br />
                    我们会在 1 个工作日内回复。
                  </p>
                </div>
              ) : (
                <form
                  id="contact-consultation"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  {/* Name + Company */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-[#0F172A] mb-1.5">
                        姓名 <span className="text-[#F97316]">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="你的姓名"
                        className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm text-[#0F172A] placeholder-slate-400 focus:outline-none focus:border-[#0F172A] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#0F172A] mb-1.5">
                        公司名称
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="所在公司"
                        className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm text-[#0F172A] placeholder-slate-400 focus:outline-none focus:border-[#0F172A] transition-colors"
                      />
                    </div>
                  </div>

                  {/* Email + Phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-[#0F172A] mb-1.5">
                        邮箱 <span className="text-[#F97316]">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm text-[#0F172A] placeholder-slate-400 focus:outline-none focus:border-[#0F172A] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#0F172A] mb-1.5">
                        手机号码
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="138 0000 0000"
                        className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm text-[#0F172A] placeholder-slate-400 focus:outline-none focus:border-[#0F172A] transition-colors"
                      />
                    </div>
                  </div>

                  {/* Team Size + Cooperation Type */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-[#0F172A] mb-1.5">
                        团队规模
                      </label>
                      <select
                        name="teamSize"
                        value={formData.teamSize}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm text-[#0F172A] focus:outline-none focus:border-[#0F172A] transition-colors bg-white cursor-pointer"
                      >
                        <option value="">请选择</option>
                        {teamSizes.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#0F172A] mb-1.5">
                        合作类型
                      </label>
                      <select
                        name="cooperationType"
                        value={formData.cooperationType}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm text-[#0F172A] focus:outline-none focus:border-[#0F172A] transition-colors bg-white cursor-pointer"
                      >
                        <option value="">请选择</option>
                        {cooperationTypes.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-xs font-medium text-[#0F172A] mb-1.5">
                      你想聊什么？
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      maxLength={500}
                      placeholder="简单描述你的现状、遇到的问题，或者你想了解的方向……"
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm text-[#0F172A] placeholder-slate-400 focus:outline-none focus:border-[#0F172A] transition-colors resize-none"
                    />
                    <div className="flex justify-end mt-1">
                      <span className={`text-xs ${charCount >= 500 ? 'text-red-400' : 'text-slate-400'}`}>
                        {charCount}/500
                      </span>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 rounded-lg bg-[#F97316] text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-[#ea6c0a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? '提交中…' : '提交预约'}
                  </button>

                  <p className="text-xs text-[#94A3B8] text-center">
                    提交即表示你同意我们通过邮件与你联系，我们不会将你的信息用于其他用途。
                  </p>
                </form>
              )}
            </div>

            {/* Right: Map + Extra Info */}
            <div
              ref={(el) => { sectionRefs.current[2] = el; }}
              className="opacity-0 translate-y-8 transition-all duration-700 delay-150 space-y-6"
            >
              {/* WeChat QR Code */}
              <div className="bg-white rounded-2xl p-6 border border-slate-100">
                <h3 className="text-sm font-bold text-[#0F172A] mb-4 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-wechat-line text-[#22C55E]" />
                  </div>
                  微信公众号
                </h3>
                <div className="flex items-center gap-4">
                  <img src="/logos/qrcode_with_logo.png" alt="宽思智能公众号" className="w-28 h-28 rounded-lg border border-slate-100" />
                  <div>
                    <p className="text-sm font-medium text-[#0F172A] mb-1">宽思智能</p>
                    <p className="text-xs text-[#94A3B8]">扫码关注获取最新动态</p>
                  </div>
                </div>
              </div>

              {/* Response Promise */}
              <div className="bg-[#0F172A] rounded-2xl p-6 text-white">
                <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-shield-check-line text-[#F97316]" />
                  </div>
                  我们的承诺
                </h3>
                <ul className="space-y-2.5">
                  {[
                    '1 个工作日内回复所有咨询',
                    '首次沟通不推销，只了解你的情况',
                    '不适合合作会直接告知，不浪费彼此时间',
                    '所有沟通内容严格保密',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-white/70">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                        <i className="ri-check-line text-[#F97316] text-xs" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Strip */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div
            ref={(el) => { sectionRefs.current[3] = el; }}
            className="opacity-0 translate-y-8 transition-all duration-700"
          >
            <h2 className="text-xl font-bold text-[#0F172A] mb-8 text-center">常见问题</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[
                {
                  q: '我们公司规模很小，适合合作吗？',
                  a: '规模不是关键，关键是你是否意识到 AI 带来的生产方式变化，并且有意愿推动改变。我们服务过 5 人团队，也服务过 200 人团队。',
                },
                {
                  q: '合作周期一般多长？',
                  a: '根据合作模式不同，从 4 周的快速诊断到 6 个月的深度陪跑都有。首次沟通后我们会根据你的情况给出建议。',
                },
                {
                  q: '我们已经买了 AI 工具，还需要你们吗？',
                  a: '工具是起点，不是终点。大多数企业的问题不在于没有工具，而在于工程环境没有重构、团队能力没有迁移。这正是我们专注的方向。',
                },
                {
                  q: '合作费用大概是什么量级？',
                  a: '不同服务模式差异较大。首次沟通完全免费，我们会在了解你的情况后给出透明的报价方案，不存在隐性费用。',
                },
              ].map((faq) => (
                <div key={faq.q} className="bg-white rounded-2xl p-6 border border-slate-100">
                  <p className="text-sm font-semibold text-[#0F172A] mb-2 flex items-start gap-2">
                    <span className="text-[#F97316] shrink-0 mt-0.5">Q</span>
                    {faq.q}
                  </p>
                  <p className="text-sm text-[#64748B] leading-relaxed pl-5">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
